import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export function EditTransactionDialog({ tx, rows = [], classes = [], open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (tx) {
      setForm({
        date: (tx.date || "").slice(0, 10),
        type: tx.type || "",
        share_class: tx.share_class || "",
        shares: tx.shares ?? "",
        price_per_share: tx.price_per_share ?? "",
        from_name: tx.from_name || "",
        to_name: tx.to_name || "",
        note: tx.note || "",
      });
    }
  }, [tx]);

  const submit = async () => {
    if (!form.share_class || !form.to_name || !form.shares || Number(form.shares) <= 0) {
      return toast.error("Fyll inn aksjeklasse, mottaker og positivt antall aksjer");
    }
    setSaving(true);
    try {
      await api.patch(`/admin/transactions/${tx.id}`, {
        date: form.date, type: form.type, share_class: form.share_class,
        shares: parseInt(form.shares, 10), price_per_share: parseFloat(form.price_per_share || 0),
        from_name: form.from_name, to_name: form.to_name, note: form.note,
      });
      toast.success("Transaksjonen er rettet og aksjeboka oppdatert");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="edit-tx-dialog">
        <DialogHeader>
          <DialogTitle>Rett transaksjon</DialogTitle>
          <DialogDescription>Endre en feilregistrert transaksjon. Aksjebeholdningen korrigeres automatisk, og endringen logges i Endringslogg.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Dato</Label><Input type="date" className="mt-1" data-testid="edit-tx-date" value={form.date} onChange={(e) => set("date", e.target.value)} /></div>
            <div><Label className="text-xs">Type</Label><Input className="mt-1" data-testid="edit-tx-type" value={form.type} onChange={(e) => set("type", e.target.value)} /></div>
          </div>
          <div><Label className="text-xs">Aksjeklasse</Label><Input className="mt-1" list="edit-tx-classes" data-testid="edit-tx-class" value={form.share_class} onChange={(e) => set("share_class", e.target.value)} />
            <datalist id="edit-tx-classes">{classes.map((c) => <option key={c.name} value={c.name} />)}</datalist>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Antall aksjer</Label><Input type="number" className="mt-1" data-testid="edit-tx-shares" value={form.shares} onChange={(e) => set("shares", e.target.value)} /></div>
            <div><Label className="text-xs">Kurs per aksje (kr)</Label><Input type="number" className="mt-1" data-testid="edit-tx-price" value={form.price_per_share} onChange={(e) => set("price_per_share", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Fra (selger)</Label>
              <Input className="mt-1" list="edit-tx-names" data-testid="edit-tx-from" value={form.from_name} onChange={(e) => set("from_name", e.target.value)} placeholder="Tom = nyutstedelse" />
            </div>
            <div>
              <Label className="text-xs">Til (mottaker)</Label>
              <Input className="mt-1" list="edit-tx-names" data-testid="edit-tx-to" value={form.to_name} onChange={(e) => set("to_name", e.target.value)} />
            </div>
          </div>
          <datalist id="edit-tx-names">{rows.map((r) => <option key={r.id} value={r.name} />)}</datalist>
          <div><Label className="text-xs">Merknad</Label><Textarea className="mt-1" rows={2} data-testid="edit-tx-note" value={form.note} onChange={(e) => set("note", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button onClick={submit} disabled={saving} className="gap-2" data-testid="edit-tx-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Lagre retting
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
